import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Button } from 'primeng/button';
import { SelectButton } from 'primeng/selectbutton';
import { Toolbar } from 'primeng/toolbar';
import { LogService } from '@wheere-front/shared-util';
import { TrackersV7Store } from '../data/trackers-v7.store';

interface LevelOption {
  name: string;
  value: number;
  match: string;
}

const LEVELS: LevelOption[] = [
  { name: 'Info', value: 1, match: 'INF' },
  { name: 'Warn', value: 2, match: 'WRN' },
  { name: 'Debug', value: 3, match: 'DBG' },
  { name: 'Error', value: 4, match: 'ERR' },
];

/**
 * V7 Tracker live-logs component — same shape as `GatewayLogsLiveComponent`, just for the
 * tracker side. Pure consumer: it reads {@code TrackersV7Store.liveLogs}, which is fed by
 * the page-level WebSocket subscription on {@code /topic/tracker/{id}/logs}.
 *
 * <p>Owning the buffer in the store (not here) means logs keep accumulating while the user
 * is on a different tab — switching back to Logs shows the full session, not just lines
 * captured while the tab was visible. The component still owns transient view state
 * (level filter, scroll stickiness, download/clear).
 */
@Component({
  selector: 'lib-tracker-logs-live',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, Button, SelectButton, Toolbar],
  template: `
    <div class="flex h-full flex-col">
      <p-toolbar class="!rounded-none !border-0 !border-b !border-gray-200">
        <ng-template #start>
          <div class="flex flex-wrap items-center gap-2">
            <p-selectButton
              [options]="levels"
              [(ngModel)]="selectedLevels"
              [multiple]="true"
              optionLabel="name"
              optionValue="value"
              size="small">
            </p-selectButton>
            <span class="text-xs font-semibold text-gray-500">
              {{ filteredLogs().length }} / {{ logs().length }} lines
            </span>
          </div>
        </ng-template>
        <ng-template #end>
          <div class="flex gap-2">
            <p-button
              label="Download"
              icon="pi pi-download"
              size="small"
              severity="secondary"
              [disabled]="!logs().length"
              (click)="onDownload()">
            </p-button>
            <p-button
              label="Clear"
              icon="pi pi-trash"
              size="small"
              severity="danger"
              [disabled]="!logs().length"
              (click)="onClear()">
            </p-button>
          </div>
        </ng-template>
      </p-toolbar>

      <div #terminal class="terminal" (scroll)="onTerminalScroll($event)">
        @if (formattedLogs().length === 0) {
          <div class="flex h-full items-center justify-center text-gray-500">
            @if (logs().length === 0) {
              No logs yet — waiting for the tracker to publish.
            } @else {
              No log line matches the current level filter.
            }
          </div>
        } @else {
          @for (html of formattedLogs(); track $index) {
            <div class="terminal-line" [innerHTML]="html"></div>
          }
        }
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
      }
      .terminal {
        flex: 1;
        background: #0f0f0f;
        color: #e5e7eb;
        font-family: Consolas, 'Courier New', Courier, monospace;
        font-size: 12px;
        line-height: 1.55;
        padding: 12px 14px;
        overflow: auto;
        white-space: pre-wrap;
        word-break: break-all;
      }
      .terminal-line {
        white-space: pre-wrap;
      }
      :host ::ng-deep .terminal-line .timestamp {
        color: #9ca3af;
      }
      :host ::ng-deep .terminal-line .brackets {
        color: #6b7280;
      }
    `,
  ],
})
export class TrackerLogsLiveComponent implements AfterViewChecked {
  trackerId = input.required<string>();
  deviceId = input<string>('');
  /** Optional seed: if {@code Tracker.lastLog} is available before the WS sub fires, we
   * push it once into the store buffer so the terminal isn't blank during the WS warm-up. */
  initialLog = input<string | undefined | null>(null);

  #store = inject(TrackersV7Store);

  /** Read-through to the store buffer — the page-level subscription owns the source. */
  protected readonly logs = this.#store.liveLogs;
  protected selectedLevels: number[] = [];
  protected readonly levels = LEVELS;

  protected readonly filteredLogs = computed(() => {
    const all = this.logs();
    if (this.selectedLevels.length === 0) return all;
    const matches = this.selectedLevels
      .map(v => LEVELS.find(l => l.value === v)?.match)
      .filter((m): m is string => !!m);
    return all.filter(line => matches.some(m => line.includes(m)));
  });

  /** Pre-formatted lines: ANSI escape codes converted to coloured spans + timestamp/bracket
   * spans for styling. Renders via {@code [innerHTML]}. */
  protected readonly formattedLogs = computed<SafeHtml[]>(() =>
    this.filteredLogs().map(line => this.#sanitizer.bypassSecurityTrustHtml(this.#logService.formatLogLineRaw(line)))
  );

  private readonly terminal = viewChild<ElementRef<HTMLDivElement>>('terminal');

  private stickToBottom = true;
  private forceScrollNext = false;
  private lastLineCount = 0;

  #logService = inject(LogService);
  #sanitizer = inject(DomSanitizer);

  constructor() {
    // Seed the store once when {@code initialLog} arrives — only if the buffer is still
    // empty. Once the WS is rolling, ignore late blob arrivals to avoid duplicates.
    effect(
      () => {
        const blob = this.initialLog();
        if (!blob) return;
        const seed = blob.split(/\r?\n/).filter(l => l.length > 0);
        if (!seed.length) return;
        if (this.logs().length === 0) {
          this.#store.appendLiveLogs(seed);
          this.forceScrollNext = true;
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngAfterViewChecked(): void {
    const el = this.terminal()?.nativeElement;
    if (!el) return;
    const lineCount = this.filteredLogs().length;
    const grew = lineCount > this.lastLineCount;
    if ((this.stickToBottom && grew) || this.forceScrollNext) {
      el.scrollTop = el.scrollHeight;
      this.forceScrollNext = false;
    }
    this.lastLineCount = lineCount;
  }

  protected onTerminalScroll(e: Event): void {
    const el = e.target as HTMLDivElement;
    this.stickToBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 32;
  }

  protected onClear(): void {
    this.#store.clearLiveLogs();
    this.forceScrollNext = true;
  }

  protected onDownload(): void {
    const blob = new Blob([this.logs().join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    a.download = `${this.deviceId() || this.trackerId()}-logs-${stamp}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
}
